import { Collapse } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Col, ListGroup, ListGroupItem, Row } from 'reactstrap';

import { TranslationsKeys } from '../../../../locales/translations';
import { StudyPlanListItem } from '../api';
import { TotalHoursTooltip } from './TotalHoursTooltip';

interface StudyPlanDetailProps {
  studyPlan: StudyPlanListItem;
}

export function StudyPlanDetail({ studyPlan }: StudyPlanDetailProps) {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_STUDY_PLANS,
  );

  const units = useMemo(() => {
    if (!studyPlan.versions || studyPlan.versions.length === 0) {
      return [];
    }

    const latestVersion = studyPlan.versions[0];
    if (!latestVersion.curriculums || latestVersion.curriculums.length === 0) {
      return [];
    }

    const curriculum = latestVersion.curriculums[0];
    if (!curriculum.semesters) {
      return [];
    }

    return curriculum.semesters
      .sort((a, b) => a.order - b.order)
      .map((semester) => ({
        name: semester.name,
        subjects: semester.semesterCourses?.map((sc) => sc.course) || [],
      }));
  }, [studyPlan.versions]);

  return (
    <div className="p-4 bg-light">
      <Row className="mb-5">
        <Col md={12}>
          <div>
            <b>{t('detail.description')}:</b>
          </div>

          <div className="mb-3">
            <TotalHoursTooltip
              studyPlanId={studyPlan.id}
              totalHours={studyPlan.studyPlanCe?.hoursTotal || null}
              activityTypeHours={studyPlan.studyPlanCe?.activityTypeHours || []}
            />
          </div>
          <div>
            {studyPlan.studyPlanCe?.description && (
              <>{studyPlan.studyPlanCe.description}</>
            )}
          </div>
        </Col>
      </Row>

      {units.length > 0 && (
        <Row className="mb-3">
          <Col md={12}>
            <div className="mb-3">
              <b>{t('detail.subjects')}:</b>
              <div className="mt-2">
                {units.map((unit, index) => (
                  <div key={index} className="mb-2">
                    <Collapse
                      headerStyles={{ height: 50 }}
                      renderTitle={() => <p>{unit.name}</p>}
                    >
                      <ListGroup>
                        {unit.subjects.length > 0 ? (
                          unit.subjects.map((subject) => (
                            <ListGroupItem
                              key={subject.id}
                              className="d-flex justify-content-between align-items-center"
                            >
                              <span>{subject.name}</span>
                              <Badge color="secondary" pill>
                                {subject.code}
                              </Badge>
                            </ListGroupItem>
                          ))
                        ) : (
                          <ListGroupItem className="text-muted">
                            {t('detail.noSubjects')}
                          </ListGroupItem>
                        )}
                      </ListGroup>
                    </Collapse>
                  </div>
                ))}
              </div>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
}
